import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { AxiosResponse } from 'axios';

import { Device, Loadable } from '../types';

type DeviceCollectionFetcher<T> = (deviceId: string) => Promise<AxiosResponse<T[]>>;

interface UseDeviceCollectionOptions<T> {
  mapper?: (items: T[]) => T[];
}

interface UseDeviceCollectionResult<T> {
  items: Loadable<T>;
  setItems: Dispatch<SetStateAction<Loadable<T>>>;
}

export function useDeviceCollection<T>(
  device: Device | undefined,
  fetchFn: DeviceCollectionFetcher<T>,
  options: UseDeviceCollectionOptions<T> = {},
): UseDeviceCollectionResult<T> {
  const [items, setItems] = useState<Loadable<T>>({ loading: true });

  const fetchRef = useRef(fetchFn);
  const mapperRef = useRef(options.mapper);
  fetchRef.current = fetchFn;
  mapperRef.current = options.mapper;

  useEffect(() => {
    if (!device) return;

    setItems({ loading: true });
    fetchRef
      .current(device.id)
      .then((response) => {
        const data = response.data;
        return mapperRef.current ? mapperRef.current(data) : data;
      })
      .then((data) => setItems({ loading: false, data }))
      .catch((err) => setItems({ loading: false, error: err }));
  }, [device]);

  return { items, setItems };
}

export default useDeviceCollection;
